import { Navigate, useNavigate, useParams } from 'react-router'

import { Show } from 'common/components/Show/Show'
import { Spinner } from 'common/components/Spinner'
import { TabPanel } from 'common/components/TabPanel/TabPanel'
import { BOOKING_STATUS_CODE } from 'common/constants/bookingConstants'
import { Shop } from 'features/Home/Book/Booking/Shop/Shop'
import { Staff } from 'features/Home/Book/Booking/Staff/Staff'
import SchedulePage from 'features/Schedule/pages/Schedule'
import { MultiplePusherProvider } from 'features/Schedule/pusher/MultiplePusherProvider'
import type { FC } from 'react'
import { memo, useEffect, useMemo } from 'react'
import { usePrevious } from 'react-use'
import { PATH_SEGMENT } from 'routes/pathSegments'
import { generateRouter, PathUtils } from 'routes/routes.utils'
import { promotionConfig } from './config'
import { Contracts } from './Contracts/Contracts'
import { Documents } from './Documents/Documents'
import { Notifications } from './Notifications/Notifications'
import { PromotionDetails } from './PromotionDetails/PromotionDetails'
import { useLazyFetchPromotionsInfoQuery } from './state/promotionsApi'

export const Promotion: FC = memo(() => {
  const navigate = useNavigate()

  const { bookingId, siteId } = useParams<{ bookingId: string; siteId: string }>()
  const previousSiteId = usePrevious(siteId)

  const [fetchPromotion, { data: promotion, isLoading, isFetching, isSuccess }] =
    useLazyFetchPromotionsInfoQuery()

  const isUnsigned = useMemo(
    () => promotion?.status_code === BOOKING_STATUS_CODE.WAITING_TO_SIGN,
    [promotion],
  )

  const routesConfig = useMemo(() => {
    const baseRoutes = {
      [PATH_SEGMENT.ROOT]: (
        <Navigate
          to={
            isUnsigned
              ? PathUtils.getPromotionContracts(siteId, bookingId)
              : PathUtils.getPromotionDetails(siteId, bookingId)
          }
          replace
        />
      ),
    }

    const additionalRoutes = isUnsigned
      ? {
          [PATH_SEGMENT.CONTRACTS]: <Contracts promotion={promotion} />,
        }
      : {
          [`${PATH_SEGMENT.DETAILS}/*`]: (
            <PromotionDetails promotion={promotion} isLoading={isLoading} isSuccess={isSuccess} />
          ),
          [PATH_SEGMENT.SCHEDULE]: (
            <MultiplePusherProvider selectedSite={siteId}>
              <SchedulePage />
            </MultiplePusherProvider>
          ),
          [`${PATH_SEGMENT.SCHEDULE}/:day`]: (
            <MultiplePusherProvider selectedSite={siteId}>
              <SchedulePage />
            </MultiplePusherProvider>
          ),
          [PATH_SEGMENT.STAFF]: <Staff />,
          [PATH_SEGMENT.CONTRACTS]: <Contracts promotion={promotion} />,
          [PATH_SEGMENT.NOTIFICATIONS]: (
            <Notifications promotion={promotion} isLoading={isLoading} />
          ),
          [PATH_SEGMENT.DOCUMENTS]: (
            <Navigate
              to={`${PathUtils.getPromotion(siteId, bookingId)}/${PATH_SEGMENT.DOCUMENTS}/${
                PATH_SEGMENT.CONTRACTS
              }`}
              replace
            />
          ),
          [`${PATH_SEGMENT.DOCUMENTS}/*`]: <Documents isPromotion />,
          [PATH_SEGMENT.SHOP]: <Shop isPromotion />,
        }

    return { ...baseRoutes, ...additionalRoutes }
  }, [promotion, isLoading, isSuccess, isUnsigned, siteId, bookingId])

  useEffect(() => {
    if (!bookingId || !siteId) {
      return
    }

    fetchPromotion({ bookingId, siteId })
  }, [bookingId, siteId])

  useEffect(() => {
    if (!!siteId && !!previousSiteId && siteId !== previousSiteId) {
      navigate(PathUtils.getPromotion(siteId, bookingId))
    }
  }, [siteId, previousSiteId, bookingId, navigate])

  return (
    <Show
      when={!!promotion && !isFetching && !isLoading}
      fallback={
        <div className='content-page-wrapper'>
          <Spinner size='large' isLoading />
        </div>
      }>
      <>
        {!isUnsigned && <TabPanel tabs={promotionConfig(promotion?.status_code).tabs} />}
        {generateRouter(routesConfig)}
      </>
    </Show>
  )
})
